import { useMutation } from '@apollo/client/react';
import { Button } from '@optools/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@optools/components/ui/dialog';
import { UPDATE_BROADCAST_SOURCE_OVERRIDE } from '@optools/services/operator-gateway/btc/mutations';
import { GET_BROADCAST_SOURCE_OVERRIDES } from '@optools/services/operator-gateway/btc/queries';
import {
  BroadcastSourceOverride,
  BroadcastSourceOverrideInput,
  BroadcastSourceOverridesQuery,
  ProgramSource,
  Region,
} from '@optools/services/operator-gateway/types/generated/graphql';
import { Pencil } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';

import {
  BroadcastSourceOverrideForm,
  BroadcastSourceOverrideFormValues,
} from './broadcast-source-override-form';

export interface UpdateBroadcastSourceOverrideProps {
  broadcastSourceOverride: BroadcastSourceOverride;
}

export function UpdateBroadcastSourceOverride({
  broadcastSourceOverride,
}: UpdateBroadcastSourceOverrideProps) {
  const [open, setIsOpen] = useState(false);

  const { id, region, ...rest } = broadcastSourceOverride;
  const defaultValues: BroadcastSourceOverrideFormValues = {
    ...rest,
    region: region ?? Region.EAST,
  };

  const [updatedBroadcastSourceOverrides, { loading }] = useMutation(
    UPDATE_BROADCAST_SOURCE_OVERRIDE,
    {
      onCompleted: ({ updateBroadcastSourceOverride }) => {
        const toastTitle = 'Update Broadcast Source Override';
        if (!updateBroadcastSourceOverride) {
          toast.error(toastTitle, {
            description: `Failed to update broadcast source override with id: ${id}`,
            duration: Infinity,
          });
          return;
        }

        toast.success(toastTitle, {
          description: `Updated broadcast source override with id: ${updateBroadcastSourceOverride.id}`,
        });

        setIsOpen(false);
      },
      update: (cache, { data }) => {
        if (!data?.updateBroadcastSourceOverride) return;

        const broadcastSourceOverrides = cache.readQuery<BroadcastSourceOverridesQuery>({
          query: GET_BROADCAST_SOURCE_OVERRIDES,
        });

        if (!broadcastSourceOverrides) return;

        cache.writeQuery<BroadcastSourceOverridesQuery>({
          data: {
            broadcastSourceOverrides: broadcastSourceOverrides.broadcastSourceOverrides.map(
              (item) =>
                item.id === data.updateBroadcastSourceOverride?.id
                  ? data.updateBroadcastSourceOverride
                  : item,
            ),
          },
          query: GET_BROADCAST_SOURCE_OVERRIDES,
        });
      },
    },
  );

  const onSubmit = (values: BroadcastSourceOverrideFormValues) => {
    const { callSign, gameSource, postgameSource, pregameSource } = values;
    const broadcastSourceOverrideInput: BroadcastSourceOverrideInput = {
      callSign,
      gameSource: gameSource === ProgramSource.UNKNOWN ? null : gameSource,
      postgameSource: postgameSource === ProgramSource.UNKNOWN ? null : postgameSource,
      pregameSource: pregameSource === ProgramSource.UNKNOWN ? null : pregameSource,
      region,
    };

    updatedBroadcastSourceOverrides({
      variables: { broadcastSourceOverrideInput, id: parseInt(id) },
    });
  };

  const onCancel = () => setIsOpen(false);

  return (
    <Dialog open={open} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        <Button aria-label="update broadcast source override" size="icon" variant="outline">
          <Pencil />
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Edit Broadcast Source Override</DialogTitle>
          <DialogDescription className="sr-only">
            Edit an existing broadcast source override
          </DialogDescription>
        </DialogHeader>
        <BroadcastSourceOverrideForm
          defaultValues={defaultValues}
          disabled={loading}
          disableRegion
          onCancel={onCancel}
          onSubmit={onSubmit}
        />
      </DialogContent>
    </Dialog>
  );
}
